import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function UpdatePreviousYearDocument() {
  const id = window.location.pathname.split("/")[2];
  const [redirect, setRedirect] = useState(false);
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });
  const [allExams, setAllExams] = useState([]);
  const [allYears, setAllYears] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [form, setForm] = useState({
    document_scope: "exam",
    exam_id: "",
    year_id: "",
    subject_id: "",
    topic_id: "",
    document_title: "",
    questions: 0,
    downloadable: "false",
    active: "true",
    notes: "",
  });
  const [documentPath, setDocumentPath] = useState("");
  const [documentFile, setDocumentFile] = useState(null);

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) setRedirect(true);
    }, 2500);
  };

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [examResp, yearResp, subjectResp, dataResp] = await Promise.all([
          axios.get(Constants.getUrls.getPyqMetaExams),
          axios.get(Constants.getUrls.getPyqMetaYears),
          axios.get(Constants.getUrls.getPyqMetaSubjects),
          axios.get(`${Constants.getUrls.getAllPreviousYearDocuments}/${id}`),
        ]);

        setAllExams(examResp?.data?.data || []);
        setAllYears(yearResp?.data?.data || []);
        setAllSubjects(subjectResp?.data?.data || []);

        const data = dataResp?.data?.data || {};
        const nextScope = data?.document_scope || (data?.subject_id ? "subject" : "exam");
        const nextSubjectId = data?.subject_id?._id || data?.subject_id || "";

        setForm({
          document_scope: nextScope,
          exam_id: data?.exam_id?._id || data?.exam_id || "",
          year_id: data?.year_id?._id || data?.year_id || "",
          subject_id: nextSubjectId,
          topic_id: data?.topic_id?._id || data?.topic_id || "",
          document_title: data?.document_title || "",
          questions: data?.questions || 0,
          downloadable: data?.downloadable ? "true" : "false",
          active: data?.active ? "true" : "false",
          notes: data?.notes || "",
        });
        setDocumentPath(data?.document || "");

        if (nextScope === "subject" && nextSubjectId) {
          const topicResp = await axios.get(
            `${Constants.getUrls.getPyqMetaTopics}?subject_id=${nextSubjectId}`
          );
          setAllTopics(topicResp?.data?.data || []);
        }
      } catch (error) {
        showMessage("Unable to load PDF resource.", "error");
      }
    };
    if (id) {
      loadInitial();
    }
  }, [id]);

  useEffect(() => {
    const loadTopics = async () => {
      if (form.document_scope !== "subject" || !form.subject_id) {
        setAllTopics([]);
        return;
      }

      try {
        const topicResp = await axios.get(
          `${Constants.getUrls.getPyqMetaTopics}?subject_id=${form.subject_id}`
        );
        setAllTopics(topicResp?.data?.data || []);
      } catch (error) {
        setAllTopics([]);
        showMessage("Unable to load topics for the selected subject.", "error");
      }
    };

    loadTopics();
  }, [form.document_scope, form.subject_id]);

  const scopeDescription = useMemo(
    () =>
      form.document_scope === "exam"
        ? "This PDF will be linked to an exam and year."
        : "This PDF will be linked to a subject and optionally a topic.",
    [form.document_scope]
  );

  const handleFieldChange = (field, value) => {
    setForm((prev) => {
      if (field === "document_scope") {
        return {
          ...prev,
          document_scope: value,
          exam_id: "",
          year_id: "",
          subject_id: "",
          topic_id: "",
        };
      }

      if (field === "subject_id") {
        return {
          ...prev,
          subject_id: value,
          topic_id: "",
        };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    if (documentFile) {
      formData.append("document", documentFile);
    }
    try {
      await axios.post(`${Constants.putUrls.updatePreviousYearDocument}${id}`, formData);
      showMessage("PDF resource updated successfully!", "success", true);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update PDF resource.", "error");
    }
  };

  if (redirect) return <Navigate to="/previous-year-documents" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">
              Update PDF Resource
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="PDF Scope"
                    value={form.document_scope}
                    onChange={(e) => handleFieldChange("document_scope", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="exam">Exam Wise</MenuItem>
                    <MenuItem value="subject">Subject Wise</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth label="Scope Note" value={scopeDescription} InputProps={{ readOnly: true }} />
                </Grid>

                {form.document_scope === "exam" ? (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Exam"
                        value={form.exam_id}
                        onChange={(e) => handleFieldChange("exam_id", e.target.value)}
                        {...selectFieldProps}
                      >
                        <MenuItem value="">Select Exam</MenuItem>
                        {allExams.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.exam_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Year"
                        value={form.year_id}
                        onChange={(e) => handleFieldChange("year_id", e.target.value)}
                        {...selectFieldProps}
                      >
                        <MenuItem value="">Select Year</MenuItem>
                        {allYears.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.year}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Subject"
                        value={form.subject_id}
                        onChange={(e) => handleFieldChange("subject_id", e.target.value)}
                        {...selectFieldProps}
                      >
                        <MenuItem value="">Select Subject</MenuItem>
                        {allSubjects.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.subject_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Topic (Optional)"
                        value={form.topic_id}
                        onChange={(e) => handleFieldChange("topic_id", e.target.value)}
                        {...selectFieldProps}
                      >
                        <MenuItem value="">Select Topic</MenuItem>
                        {allTopics.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.topic_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Document Title"
                    value={form.document_title}
                    onChange={(e) => handleFieldChange("document_title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Questions"
                    value={form.questions}
                    onChange={(e) => handleFieldChange("questions", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Downloadable"
                    value={form.downloadable}
                    onChange={(e) => handleFieldChange("downloadable", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Active"
                    value={form.active}
                    onChange={(e) => handleFieldChange("active", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="file"
                    label="PDF Document"
                    focused
                    fullWidth
                    onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={form.notes}
                    onChange={(e) => handleFieldChange("notes", e.target.value)}
                  />
                </Grid>
              </Grid>
              {documentPath ? (
                <MDBox mt={2}>
                  <a href={`${Constants.imageDomain}${documentPath}`} target="_blank" rel="noreferrer">
                    Open current PDF
                  </a>
                </MDBox>
              ) : null}
              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  Update
                </MDButton>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar color={notification.color} icon="notifications" title="AgriQuiz" content={notification.message} dateTime="Just now" open={notification.open} />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UpdatePreviousYearDocument;
